import './styles/buttons.css';
import { isContractDisabled, getStatusColor } from './utils/formatters/contractStatus';
import React, { useState, useEffect, useCallback, Fragment } from 'react';
import ConfirmationModal from '../../templates/ConfirmationModal';
import { useSignal } from "@preact/signals-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import PageForm from "../../templates/PageForm";
import CurrencyInput from "../../molecules/CurrencyInput";
import TextEdit from "../../molecules/TextEdit";
import BadgeStatus from "../../atoms/BadgeStatus";
import ArtifactFilesUpload from './components/files/ArtifactFilesUpload';
import { EMPTY_BILLING_CONFIG } from './components/form/BillingConfigSection';
import BillingStatusBadge from './components/status/BillingStatusBadge';
import { useBillingStatus } from './hooks/data/useBillingStatus';
import ContractActionDropdown from './components/status/ContractActionDropdown';
import { useFileDownload } from './components/files/useFileDownload';
import { useContractActions } from './hooks/contract/useContractActions';
import { useContractSubmit } from './hooks/contract/useContractSubmit';
import { BaseCalculation, BillingConfig, DisbursementItem } from './types/operations';
import { ErrorField } from '../../../types/ErrorField';
import { getErrorMessage } from '../../../utils/formErrorUtils';
import { applyDateMask } from '../../../utils/dateMask';
import appConfig from '../../../config/app-config';

const { address: API_URL, key: API_KEY } = appConfig.apis.contractsManagementApiEnv;
const CONTRACTS_LIST_URL = '/contracts/0/100/id/ASC/s=;';

// ============================================
// TYPES
// ============================================

interface UnderlyingReceivable {
    id: string;
    cedente: string;
    cnpj_cedente: string;
    sacado: string;
    cnpj_sacado: string;
    emissao: string;
    vencimento: string;
    valor_vencimento: number;
    valor_desembolso: number;
    prazo_dias: number;
    taxa_am: number;
    tir_aa: number;
    tir_am: number;
    spread_aa: number;
    vpl: number;
    margem_vpl: number;
    duration: number;
}

interface DraweeRiskForm {
    operation_number: string;
    sequencial_number: number;
    status?: string;
    emission_date: string;
    expiration_date: string;
    approved_on: string;
    external_link: string;
    program_name: string;
    loan_amount: number;
    total_amount: number;
    nominal_value: number;
    rebate_amount: number;
    guarantee_percentage: number;
    assignor_name: string;
    assignor_document: string;
    drawee_name: string;
    drawee_document: string;
    disbursement: { disbursements: DisbursementItem[]; totalAmount: number };
    underlying_receivables: {
        items: UnderlyingReceivable[];
        total_vencimento: number;
        total_desembolso: number;
        quantity: number;
        cdi_aa: number;
    };
    base_calculation?: BaseCalculation;
    billing_config?: BillingConfig;
    financial_params?: {
        funding_rate: number | null;
        platform_fee_rate: number | null;
        grossup_tax_rate: number | null;
    };
    assignor_billing_address?: {
        name: string;
        document_number: string;
        address_street: string;
        address_number: string;
        address_complement: string;
        address_neighborhood: string;
        address_city: string;
        address_state: string;
        address_zip_code: string;
    };
    main_risk?: {
        name: string;
        uuid: string | null;
        document: string;
    };
}

const EMPTY_FORM: DraweeRiskForm = {
    operation_number: '',
    sequencial_number: 0,
    status: '',
    emission_date: '',
    expiration_date: '',
    approved_on: '',
    external_link: '',
    program_name: '',
    loan_amount: 0,
    total_amount: 0,
    nominal_value: 0,
    rebate_amount: 0,
    guarantee_percentage: 0,
    assignor_name: '',
    assignor_document: '',
    drawee_name: '',
    drawee_document: '',
    disbursement: { disbursements: [], totalAmount: 0 },
    underlying_receivables: { items: [], total_vencimento: 0, total_desembolso: 0, quantity: 0, cdi_aa: 0 },
    base_calculation: { tir_annual: null, tir_monthly: null, spread_annual: null, vpl: null, vpl_margin: null, duration_days: null },
    billing_config: EMPTY_BILLING_CONFIG,
    financial_params: undefined,
    assignor_billing_address: undefined,
    main_risk: undefined,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const formatCurrency = (value: number | null | undefined): string =>
    (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDateISO = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = dateStr.split('T')[0];
    const parts = date.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
};

// ============================================
// MAIN COMPONENT
// ============================================

const DraweeRiskViewPage: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const credit_operation_id = params.id;
    const { handleDownload } = useFileDownload();
    const isLoading = useSignal(false);
    const errorFields = useSignal<ErrorField[]>([]);
    const apiVersion = useSignal('');
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isContractLoaded, setIsContractLoaded] = useState(false);
    const [billingStatus, setBillingStatus] = useState<{ billed?: boolean; billing_info?: string }>({});
    const { billingData, isPolling, startPolling, fetchBillingStatus } = useBillingStatus(credit_operation_id);
    const [existingContractFile, setExistingContractFile] = useState<string>('');
    const [existingArtifactFiles, setExistingArtifactFiles] = useState<string[]>([]);
    const [removedArtifactFiles, setRemovedArtifactFiles] = useState<string[]>([]);
    const artifactFiles = useSignal<File[]>([]);
    const [participants, setParticipants] = useState<any[]>([]);

    const form = useSignal<DraweeRiskForm>(EMPTY_FORM);

    const handleChange = useCallback((field: keyof DraweeRiskForm, value: any) => {
        form.value = { ...form.value, [field]: value };
        setHasUnsavedChanges(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validateAllFields = useCallback((): boolean => {
        const f = form.value;
        const missing: string[] = [];
        if (!f.external_link?.trim()) missing.push('Card JIRA');
        if (!f.emission_date?.trim()) missing.push('Data de Emissão');
        if (!f.disbursement.totalAmount || f.disbursement.totalAmount <= 0) missing.push('Desembolsos');
        if (f.billing_config?.generate_billets === null) missing.push('Gerar boletos para cobrança');
        if (f.billing_config?.automatic_preventive_collection === null) missing.push('Cobrança preventiva automática');
        if (f.billing_config?.automatic_overdue_collection === null) missing.push('Cobrança de vencidos automática');

        if (missing.length > 0) {
            errorFields.value = missing.map(m => ({ fieldName: m, message: 'Campo obrigatório' }));
            toast.error(`Campos obrigatórios: ${missing.join(', ')}`);
            return false;
        }
        errorFields.value = [];
        return true;
    }, [form, errorFields]);

    const { handleCancelContract, handleFinishContract, handleBillingProcess } = useContractActions({
        apiUrl: API_URL || '',
        apiKey: API_KEY || '',
        creditOperationId: credit_operation_id,
        setLoading: (v: boolean) => { isLoading.value = v; },
        hasUnsavedChanges,
        hasInvalidDueDates: false,
        validateAllFields,
        setIsCancelModalOpen,
        setIsFinishModalOpen,
        onRefresh: () => { fetchContractData(); startPolling(); },
    });

    const { submitContract } = useContractSubmit({
        apiUrl: API_URL,
        apiKey: API_KEY,
        endpoint: '/contracts/receivables_assignment/update',
        setLoading: (v: boolean) => { isLoading.value = v; },
    });

    const fetchContractData = async () => {
        if (!credit_operation_id) return;
        try {
            isLoading.value = true;
            const token = (await Auth.currentSession()).getIdToken().getJwtToken();
            const response = await fetch(`${API_URL}/contracts/receivables_assignment/${credit_operation_id}`, {
                headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': API_KEY || '' },
            });
            if (!response.ok) { toast.error('Erro ao carregar operação'); return; }

            const data = await response.json();
            const schema = data.operation_dictionary || {};

            apiVersion.value = data.apiVersion || '';
            setBillingStatus({ billed: data.billed, billing_info: data.billing_info });

            // Map files
            if (schema.files) {
                setExistingContractFile(schema.files.contract_file || '');
                setExistingArtifactFiles(schema.files.artefacts || []);
            }

            // Map participants
            const assignor = data.participants?.find((p: any) => p.participant_type_name === 'ASSIGNOR');
            const drawee = data.participants?.find((p: any) => p.participant_type_name === 'DRAWEE');
            setParticipants(data.participants || []);

            form.value = {
                operation_number: data.operation_number || '',
                sequencial_number: data.sequencial_number || 0,
                status: data.status || '',
                emission_date: formatDateISO(schema.emission_date || data.emission_date || ''),
                expiration_date: formatDateISO(schema.expiration_date || data.expiration_date || ''),
                approved_on: formatDateISO(data.approved_on || ''),
                external_link: schema.external_link || '',
                program_name: schema.program_name || '',
                loan_amount: data.loan_amount || schema.loan_amount || 0,
                total_amount: schema.total_amount || data.nominal_value || 0,
                nominal_value: data.nominal_value || 0,
                rebate_amount: schema.rebate_amount || 0,
                guarantee_percentage: schema.guarantee_percentage || 0,
                assignor_name: assignor?.name || '',
                assignor_document: assignor?.document || '',
                drawee_name: drawee?.name || '',
                drawee_document: drawee?.document || '',
                disbursement: {
                    disbursements: schema.disbursement?.disbursements || [],
                    totalAmount: schema.disbursement?.totalAmount || 0,
                },
                underlying_receivables: schema.underlying_receivables || { items: [], total_vencimento: 0, total_desembolso: 0, quantity: 0, cdi_aa: 0 },
                base_calculation: schema.base_calculation || EMPTY_FORM.base_calculation,
                billing_config: schema.billing_config || EMPTY_BILLING_CONFIG,
                financial_params: schema.financial_params || undefined,
                assignor_billing_address: schema.assignor_billing_address || undefined,
                main_risk: schema.main_risk || undefined,
            };
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Erro ao conectar';
            toast.error(msg);
        } finally {
            isLoading.value = false;
            setHasUnsavedChanges(false);
            setIsContractLoaded(true);
        }
    };

    const handleSubmit = async () => {
        if (isFormDisabled) { toast.error('Status não permite alterações.'); return; }
        if (!form.value.external_link?.trim()) { toast.error('Card JIRA é obrigatório'); return; }

        try {
            isLoading.value = true;
            const token = (await Auth.currentSession()).getIdToken().getJwtToken();
            const f = form.value;

            const contractFileName = existingContractFile;

            const payload = {
                type: 'RECEIVABLES_ASSIGNMENT',
                operation_type: 'RECEIVABLES_ASSIGNMENT',
                credit_operation_id,
                operation_number: f.operation_number,
                credit_product_type_name: 'RISCO_SACADO',
                operation_schema_id: 5,
                nominal_value: f.nominal_value,
                expiration_date: f.expiration_date ? f.expiration_date.split('/').reverse().join('-') : null,
                participants,
                operation_dictionary: {
                    operation_number: f.operation_number,
                    emission_date: f.emission_date ? f.emission_date.split('/').reverse().join('-') : '',
                    expiration_date: f.expiration_date ? f.expiration_date.split('/').reverse().join('-') : '',
                    external_link: f.external_link,
                    credit_product_type: 'RISCO_SACADO',
                    program_name: f.program_name,
                    loan_amount: f.loan_amount,
                    total_amount: f.total_amount,
                    rebate_amount: f.rebate_amount,
                    guarantee_percentage: f.guarantee_percentage,
                    disbursement: f.disbursement,
                    underlying_receivables: f.underlying_receivables,
                    billing_config: f.billing_config,
                    base_calculation: f.base_calculation,
                    financial_params: f.financial_params,
                    assignor_billing_address: f.assignor_billing_address,
                    main_risk: f.main_risk,
                    files: {
                        contract_file: contractFileName,
                        artefacts: [
                            ...existingArtifactFiles.filter(file => !removedArtifactFiles.includes(file)),
                            ...artifactFiles.value.map(file => file.name),
                        ],
                    },
                },
            };

            await submitContract(
                payload,
                { artifactFiles: artifactFiles.value },
                {
                    successMessage: 'Operação de Risco Sacado salva com sucesso!',
                    onSuccess: async () => {
                        setHasUnsavedChanges(false);
                        await fetchContractData();
                        artifactFiles.value = [];
                        setRemovedArtifactFiles([]);
                    },
                }
            );
        } finally {
            isLoading.value = false;
        }
    };

    useEffect(() => {
        if (credit_operation_id) fetchContractData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [credit_operation_id]);

    const isFormDisabled = isContractDisabled(form.value.status);


    return (
        <Fragment>
            <PageForm
                pageTitle="Risco Sacado"
                apiVersion={apiVersion.value}
                isLoading={isLoading.value}
                breadcrumb={[
                    { label: "Contratos", url: CONTRACTS_LIST_URL },
                    { label: "Risco Sacado", url: "#" },
                ]}
                controlSlot={
                    <div className="pt-4 flex justify-end">
                        <button type="button" className="btn-cancel" onClick={() => navigate(CONTRACTS_LIST_URL)}>
                            Voltar
                        </button>
                        <ContractActionDropdown
                            onSave={handleSubmit}
                            onCancel={() => setIsCancelModalOpen(true)}
                            onFinish={() => setIsFinishModalOpen(true)}
                            onBilling={handleBillingProcess}
                            creditOperationId={credit_operation_id}
                            apiUrl={API_URL || ''}
                            apiKey={API_KEY || ''}
                            setLoading={(v: boolean) => { isLoading.value = v; }}
                            onRegistered={() => { fetchContractData(); }}
                            isLoading={isLoading.value}
                            isFormDisabled={isFormDisabled}
                            hasContractId={!!credit_operation_id}
                            contractStatus={form.value.status || ''}
                            isBilled={!!billingStatus.billed}
                        />
                    </div>
                }
            >
                <div className="container mx-auto px-2 py-4">
                    <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Informações Gerais</h3>
                    <div className="mb-4 mt-2">
                        <BadgeStatus color={getStatusColor(form.value.status)} text={form.value.status || ''} />
                        {form.value.program_name && (
                            <span className="ml-3 text-xs font-semibold bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 px-2 py-1 rounded">
                                Programa: {form.value.program_name}
                            </span>
                        )}
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-4">
                        <div className="col-span-2">
                            <TextEdit label="Nº Contrato" value={String(form.value.sequencial_number || '')} disabled />
                        </div>
                        <div className="col-span-2">
                            <TextEdit label="Código Operação" value={form.value.operation_number}
                                onChange={(e) => handleChange('operation_number', e.target.value)} disabled={isFormDisabled} />
                        </div>
                        <div className="col-span-3">
                            <TextEdit label="Card JIRA" value={form.value.external_link}
                                onChange={(e) => handleChange('external_link', e.target.value)} disabled={isFormDisabled}
                                errorMessage={getErrorMessage(errorFields.value, 'Card JIRA')} />
                        </div>
                        <div className="col-span-2">
                            <TextEdit label="Programa" value={form.value.program_name} disabled />
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-4 mt-6">
                        <div className="col-span-5">
                            <TextEdit label="Cedente / Fornecedor" value={form.value.assignor_name} disabled />
                        </div>
                        <div className="col-span-5">
                            <TextEdit label="Sacado / Âncora" value={form.value.drawee_name} disabled />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-6">
                        <div className="col-span-5">
                            <TextEdit label="Risco Principal" value={form.value.main_risk?.name || ''} disabled />
                        </div>
                        <div className="col-span-3">
                            <TextEdit label="Documento Risco" value={form.value.main_risk?.document || ''} disabled />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 mt-6">
                        <div className="col-span-2">
                            <TextEdit label="Data de Emissão" value={form.value.emission_date}
                                onChange={(e) => handleChange('emission_date', applyDateMask(e.target.value))} disabled={isFormDisabled} />
                        </div>
                        <div className="col-span-2">
                            <TextEdit label="Data de Vencimento" value={form.value.expiration_date}
                                onChange={(e) => handleChange('expiration_date', applyDateMask(e.target.value))} disabled={isFormDisabled} />
                        </div>
                        <div className="col-span-2">
                            <TextEdit label="Data de Aprovação" value={form.value.approved_on}
                                onChange={(e) => handleChange('approved_on', applyDateMask(e.target.value))} disabled={isFormDisabled} placeholder="dd/mm/aaaa" />
                        </div>
                        <div className="col-span-2">
                            <CurrencyInput label="% Aval" prefix="%" fractionDigits={2}
                                value={form.value.guarantee_percentage} onChange={(v) => handleChange('guarantee_percentage', v)} disabled={isFormDisabled} />
                        </div>
                    </div>

                    <h3 className="text-md font-semibold mt-6 mb-3 text-gray-700 dark:text-gray-200">Valor Financiado</h3>
                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <CurrencyInput label="Valor Financiado" prefix="R$" fractionDigits={2}
                                value={form.value.loan_amount} onChange={(v) => handleChange('loan_amount', v)} disabled={isFormDisabled} />
                        </div>
                        <div className="col-span-3">
                            <CurrencyInput label="Valor Nominal" prefix="R$" fractionDigits={2}
                                value={form.value.nominal_value} onChange={(v) => handleChange('nominal_value', v)} disabled={isFormDisabled} />
                        </div>
                        <div className="col-span-3">
                            <CurrencyInput label="Valor Total" prefix="R$" fractionDigits={2}
                                value={form.value.total_amount} onChange={(v) => handleChange('total_amount', v)} disabled={isFormDisabled} />
                        </div>
                    </div>

                    {/* Cálculo Base e Parâmetros Financeiros (somente leitura) */}
                    {form.value.base_calculation && (
                        <div className="border border-gray-200 dark:border-slate-600 rounded-lg p-4 mt-4">
                            <h3 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-200">Cálculo Base</h3>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">TIR (% a.a.)</label>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{(Number(form.value.base_calculation.tir_annual) || 0).toFixed(4)}%</p>
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">TIR (% a.m.)</label>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{(Number(form.value.base_calculation.tir_monthly) || 0).toFixed(4)}%</p>
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Spread (% a.a.)</label>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{(Number(form.value.base_calculation.spread_annual) || 0).toFixed(4)}%</p>
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">VPL (R$)</label>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{formatCurrency(form.value.base_calculation.vpl)}</p>
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Margem VPL (%)</label>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{(Number(form.value.base_calculation.vpl_margin) || 0).toFixed(4)}%</p>
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Duration (dias)</label>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{Number(form.value.base_calculation.duration_days) || 0}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Parâmetros Financeiros */}
                    <div className="border border-gray-200 dark:border-slate-600 rounded-lg p-4 mt-4">
                        <h3 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-200">Parâmetros Financeiros</h3>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-2">
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">CDI (a.a.)</label>
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{((Number(form.value.underlying_receivables.cdi_aa) || 0) * 100).toFixed(4)}%</p>
                            </div>
                            {form.value.financial_params && (
                                <>
                                    <div className="col-span-2">
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Funding Rate</label>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{(Number(form.value.financial_params.funding_rate) || 0).toFixed(4)}%</p>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Taxa Plataforma</label>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{(Number(form.value.financial_params.platform_fee_rate) || 0).toFixed(4)}%</p>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Grossup</label>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{(Number(form.value.financial_params.grossup_tax_rate) || 0).toFixed(4)}%</p>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <BillingStatusBadge billed={billingStatus.billed} billingInfo={billingStatus.billing_info} billingData={billingData} isPolling={isPolling} fetchBillingStatus={fetchBillingStatus} />

                    {/* Desembolsos */}
                    {(form.value.disbursement.disbursements.length > 0 || !isFormDisabled) && (
                        <div className="border border-gray-200 dark:border-slate-600 rounded-lg p-4 mt-4">
                            <h3 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-200">Desembolsos</h3>
                            {form.value.disbursement.disbursements.length > 0 && (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 dark:border-gray-700">
                                            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Data</th>
                                            <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Valor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {form.value.disbursement.disbursements.map((d, idx) => (
                                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'}>
                                                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatDateISO(d.date)}</td>
                                                <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(d.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            <div className="grid grid-cols-12 gap-4 mt-3">
                                <div className="col-span-3">
                                    <CurrencyInput label="Valor Nominal" prefix="R$" fractionDigits={2}
                                        value={form.value.disbursement.totalAmount}
                                        onChange={(v) => handleChange('disbursement', { ...form.value.disbursement, totalAmount: v })}
                                        disabled={isFormDisabled} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Títulos (Underlying Receivables) */}
                    {form.value.underlying_receivables.quantity > 0 && (
                        <>
                            <h3 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-200">Títulos ({form.value.underlying_receivables.quantity})</h3>
                            <div className="overflow-x-auto overflow-y-auto max-h-[300px] rounded border border-gray-200 dark:border-gray-700">
                                <table className="w-full text-xs whitespace-nowrap">
                                    <thead className="bg-gray-100 dark:bg-slate-700 sticky top-0">
                                        <tr>
                                            <th className="text-left px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">Cedente</th>
                                            <th className="text-left px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">CNPJ Cedente</th>
                                            <th className="text-left px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">Sacado</th>
                                            <th className="text-left px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">CNPJ Sacado</th>
                                            <th className="text-right px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">Emissão</th>
                                            <th className="text-right px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">Vencimento</th>
                                            <th className="text-right px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">Valor Venc.</th>
                                            <th className="text-right px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">Valor Desemb.</th>
                                            <th className="text-right px-2 py-1.5 font-medium text-gray-600 dark:text-gray-300">Prazo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {form.value.underlying_receivables.items.map((item, idx) => (
                                            <tr key={item.id} className={idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'}>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100">{item.cedente}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100">{item.cnpj_cedente}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100">{item.sacado}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100">{item.cnpj_sacado}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100 text-right">{formatDateISO(item.emissao)}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100 text-right">{formatDateISO(item.vencimento)}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100 text-right">{formatCurrency(item.valor_vencimento)}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100 text-right">{formatCurrency(item.valor_desembolso)}</td>
                                                <td className="px-2 py-1.5 text-gray-900 dark:text-gray-100 text-right">{item.prazo_dias ?? '-'}d</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
                                <span>{form.value.underlying_receivables.quantity} título(s)</span>
                                <span>Total: {formatCurrency(form.value.underlying_receivables.total_vencimento)} (venc.) / {formatCurrency(form.value.underlying_receivables.total_desembolso)} (desemb.)</span>
                            </div>
                        </>
                    )}

                    {/* Endereço de Cobrança */}
                    {form.value.assignor_billing_address && (
                        <>
                            <h3 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-200">Endereço de Cobrança</h3>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-4">
                                    <TextEdit label="Nome" value={form.value.assignor_billing_address.name || ''} disabled />
                                </div>
                                <div className="col-span-2">
                                    <TextEdit label="Documento" value={form.value.assignor_billing_address.document_number || ''} disabled />
                                </div>
                                <div className="col-span-4">
                                    <TextEdit label="Logradouro" value={form.value.assignor_billing_address.address_street || ''} disabled />
                                </div>
                                <div className="col-span-2">
                                    <TextEdit label="Número" value={form.value.assignor_billing_address.address_number || ''} disabled />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4 mt-4">
                                <div className="col-span-3">
                                    <TextEdit label="Complemento" value={form.value.assignor_billing_address.address_complement || ''} disabled />
                                </div>
                                <div className="col-span-3">
                                    <TextEdit label="Bairro" value={form.value.assignor_billing_address.address_neighborhood || ''} disabled />
                                </div>
                                <div className="col-span-3">
                                    <TextEdit label="Cidade" value={form.value.assignor_billing_address.address_city || ''} disabled />
                                </div>
                                <div className="col-span-1">
                                    <TextEdit label="UF" value={form.value.assignor_billing_address.address_state || ''} disabled />
                                </div>
                                <div className="col-span-2">
                                    <TextEdit label="CEP" value={form.value.assignor_billing_address.address_zip_code || ''} disabled />
                                </div>
                            </div>
                        </>
                    )}

                    {/* Contrato */}
                    {existingContractFile && (
                        <>
                            <h3 className="text-md font-semibold mt-6 mb-3 text-gray-700 dark:text-gray-200">Contrato</h3>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => handleDownload(credit_operation_id || '', existingContractFile)}
                                    className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                                >
                                    {existingContractFile}
                                </button>
                            </div>
                        </>
                    )}

                    {/* Artefatos */}
                    <ArtifactFilesUpload
                        artifactFiles={artifactFiles}
                        existingFiles={existingArtifactFiles}
                        removedFiles={removedArtifactFiles}
                        onRemoveExisting={(file) => setRemovedArtifactFiles(prev => [...prev, file])}
                        onRestoreExisting={(file) => setRemovedArtifactFiles(prev => prev.filter(f => f !== file))}
                        creditOperationId={credit_operation_id || ''}
                        onDownload={handleDownload}
                    />
                </div>
            </PageForm>

            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onCancel={() => setIsCancelModalOpen(false)}
                onConfirm={handleCancelContract}
                id="cancel-contract-modal"
                title="Cancelar Contrato"
                text="Tem certeza que deseja cancelar este contrato?"
            />
            <ConfirmationModal
                isOpen={isFinishModalOpen}
                onCancel={() => setIsFinishModalOpen(false)}
                onConfirm={handleFinishContract}
                id="finish-contract-modal"
                title="Finalizar Contrato"
                text="Tem certeza que deseja finalizar este contrato?"
            />
        </Fragment>
    );
};

export default DraweeRiskViewPage;
